/**
 * アスペクト比 / 出力解像度まわりのモデル別サポート判定ヘルパ
 *
 * - Gemini (nano-banana) 系: API が指定 aspect_ratio を ±2% で忠実に守る。
 *   対応比率と実出力サイズは**モデル別** (3.1 Flash 系のみ極端比 1:4 / 1:8 / 4:1 / 8:1 に対応)
 * - OpenAI gpt-image-2: 両辺 16 の倍数・比率 1:3〜3:1 以内のフレキシブルサイズに対応
 *   → 範囲内の任意比率を厳密に守る (プリセットに限らない)
 *
 * バックエンドの openai_image_provider._aspect_ratio_to_size / image_generation.constants
 * (ALLOWED_ASPECT_RATIOS・GEMINI_ASPECT_RATIOS_BY_MODEL) と同じ規則を JS で再現する。
 * モデル提供側のマッピングが変わった場合は両方同期で更新すること。
 */

import type { AssetSpec } from '../../../features/media/api';

// Gemini 全モデル共通で対応するアスペクト比 (backend constants.py の ALLOWED_ASPECT_RATIOS と同期)
export const GEMINI_SUPPORTED_ASPECT_RATIOS: ReadonlySet<string> = new Set([
  '1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9',
]);

// 3.1 Flash 系 (通常 / Lite) と Nano Banana 2.1 固有の極端比。公式表 (ai.google.dev/gemini-api/docs/image-generation)
// では 3.1 Flash Image のみ 14 種と記載。Lite は公式情報内に矛盾があったため実 API で確認した:
//   2026-07-30 gemini-3.1-flash-lite-image × 1:4 × 1K → HTTP 200 / 512x2064 (比率 0.248 ≒ 1:4) で対応を確認
export const GEMINI_EXTREME_ASPECT_RATIOS: ReadonlySet<string> = new Set(['1:4', '1:8', '4:1', '8:1']);

const GEMINI_31_FLASH_ASPECT_RATIOS: ReadonlySet<string> = new Set([
  ...GEMINI_SUPPORTED_ASPECT_RATIOS, ...GEMINI_EXTREME_ASPECT_RATIOS,
]);

/**
 * Gemini モデル別の対応アスペクト比。
 * preview コードは API 送信時に GA コードへ変換される (backend/services/gemini_model_aliases.py) ため、
 * 能力判定では両コードを同一モデルとして扱う (両方を明示登録する)。
 * 未登録モデルは安全側として共通 10 種へフォールバックする。
 */
const GEMINI_ASPECT_RATIOS_BY_MODEL: Record<string, ReadonlySet<string>> = {
  'gemini-3.1-flash-image-preview': GEMINI_31_FLASH_ASPECT_RATIOS,
  'gemini-3.1-flash-image': GEMINI_31_FLASH_ASPECT_RATIOS,
  'gemini-3.1-flash-lite-image': GEMINI_31_FLASH_ASPECT_RATIOS, // 2026-07-30 実 API で 1:4 対応を確認
  'gemini-nano-banana-2.1': GEMINI_31_FLASH_ASPECT_RATIOS, // 公式表 (2026-10-08) で 3.1 Flash と同じ 14 種
  'gemini-3-pro-image-preview': GEMINI_SUPPORTED_ASPECT_RATIOS,
  'gemini-3-pro-image': GEMINI_SUPPORTED_ASPECT_RATIOS,
  'gemini-2.5-flash-image': GEMINI_SUPPORTED_ASPECT_RATIOS,
};

// gpt-image-2 は 1:3〜3:1 範囲の任意比率をネイティブ対応 (プリセット外も可)。
// 丸め候補としては Gemini 共通セットを使う。
export const OPENAI_V2_SUPPORTED_ASPECT_RATIOS: ReadonlySet<string> = GEMINI_SUPPORTED_ASPECT_RATIOS;

export type ModelProvider = 'gemini' | 'openai' | 'unknown';

export function getModelProvider(modelCode: string | undefined | null): ModelProvider {
  if (!modelCode) return 'unknown';
  if (modelCode.startsWith('gpt-image-')) return 'openai';
  if (modelCode.startsWith('gemini-')) return 'gemini';
  return 'unknown';
}

/**
 * モデルが厳密対応するアスペクト比セット。
 * Gemini はモデル別 (3.1 Flash 系のみ 14 種)、それ以外は共通 10 種。
 * gpt-image-2 の対応判定は範囲判定 (isAspectRatioSupportedBy) なのでこの関数は
 * 「丸め候補セット」としてのみ使う。
 */
export function getSupportedAspectRatios(
  modelCode: string | undefined | null,
): ReadonlySet<string> {
  if (getModelProvider(modelCode) !== 'gemini') return GEMINI_SUPPORTED_ASPECT_RATIOS;
  return GEMINI_ASPECT_RATIOS_BY_MODEL[modelCode as string] ?? GEMINI_SUPPORTED_ASPECT_RATIOS;
}

/** 'w:h' 文字列を数値比率へ。パース不能は null */
/** 'W:H' 文字列を幅/高さの比 (number) にする。解釈できなければ null */
export function parseRatioValue(s: string | undefined | null): number | null {
  return parseRatio(s);
}

function parseRatio(s: string | undefined | null): number | null {
  if (!s || s === 'none') return null;
  const [w, h] = s.split(':').map(Number);
  if (!Number.isFinite(w) || !Number.isFinite(h) || h === 0) return null;
  return w / h;
}

/** モデルが当該 aspect_ratio を厳密サポートするか */
export function isAspectRatioSupportedBy(
  modelCode: string | undefined | null,
  aspectRatio: string | undefined | null,
): boolean {
  if (!aspectRatio || aspectRatio === 'none') return true;
  const provider = getModelProvider(modelCode);
  if (provider === 'gemini') return getSupportedAspectRatios(modelCode).has(aspectRatio);
  if (provider === 'openai') {
    // gpt-image-2 は 1/3〜3/1 範囲内のあらゆる比率をネイティブ対応 (公式制約)
    const r = parseRatio(aspectRatio);
    if (r === null) return true;
    return r >= 1 / 3 && r <= 3;
  }
  return true;
}

/**
 * モデル × ユーザー選択アスペクト比から API 実出力の比率を予測する。
 * backend openai_image_provider._aspect_ratio_to_size と同じロジック。
 */
export function predictApiOutputAspectRatio(
  _modelCode: string | undefined | null,
  userAspectRatio: string | undefined | null,
): { ratio: string; approximated: boolean } {
  if (!userAspectRatio || userAspectRatio === 'none') {
    return { ratio: userAspectRatio ?? 'none', approximated: false };
  }
  // Gemini / gpt-image-2 ともにユーザー指定比率をそのまま忠実に出力する
  return { ratio: userAspectRatio, approximated: false };
}

/** ユーザー選択と API 出力の比率が一致するか（±2% 許容） */
export function doesUserAspectMatchApiOutput(
  modelCode: string | undefined | null,
  userAspectRatio: string | undefined | null,
): boolean {
  if (!userAspectRatio || userAspectRatio === 'none') return true;
  const { ratio: apiRatio } = predictApiOutputAspectRatio(modelCode, userAspectRatio);
  const u = parseRatio(userAspectRatio);
  const a = parseRatio(apiRatio);
  if (u === null || a === null) return userAspectRatio === apiRatio;
  return Math.abs(u - a) / a <= 0.02;
}

/** spec の推奨サイズ (両方正の整数のときだけ返す) */
export function getSpecRecommendedSize(
  spec: AssetSpec | null | undefined,
): { width: number; height: number } | null {
  if (!spec) return null;
  const w = (spec as { recommended_width_px?: number | null }).recommended_width_px;
  const h = (spec as { recommended_height_px?: number | null }).recommended_height_px;
  if (!w || !h || w <= 0 || h <= 0) return null;
  return { width: w, height: h };
}

/**
 * spec のピクセル指定から初期カスタム解像度を導出。
 * 推奨サイズがあればそれを目標にする (min 系 spec で auto_trim ON のとき、出力が常に最小値に
 * なってしまうのを避ける。docs/requirements/media-spec-recommended-size-warning-plan.md §3.4)。
 * - fixed: width_px (推奨は原則 NULL)
 * - longest_edge_range: 推奨長辺と spec 比率 (あれば) から幅・高さを導出。比率が無ければ目標なし
 * - それ以外: recommended ?? width_px ?? min ?? max
 */
export function deriveCustomResolutionFromSpec(
  spec: AssetSpec | null | undefined,
): { width?: number; height?: number; aspectRatio?: string } {
  if (!spec) return {};
  const t = spec.size_constraint_type;
  const firstRatio = spec.allowed_aspect_ratios?.[0];
  const recommended = getSpecRecommendedSize(spec);
  if (t === 'aspect_ratio_only') {
    // 比率のみの規格でも推奨があれば目標にする (無いと auto_trim ON で標準サイズへ縮小され、推奨未達になる)
    return recommended
      ? { width: recommended.width, height: recommended.height, aspectRatio: firstRatio }
      : { aspectRatio: firstRatio };
  }
  if (t === 'longest_edge_range') {
    if (!recommended) return { aspectRatio: firstRatio };
    const ratioStr = getSpecRatioString(spec);
    const r = ratioStr ? parseRatio(ratioStr) : null;
    if (r === null) return { aspectRatio: firstRatio };
    const edge = recommended.width;
    return r >= 1
      ? { width: edge, height: Math.round(edge / r), aspectRatio: firstRatio }
      : { width: Math.round(edge * r), height: edge, aspectRatio: firstRatio };
  }
  if (t !== 'fixed' && recommended) {
    return { width: recommended.width, height: recommended.height, aspectRatio: firstRatio };
  }
  // ピクセル指定がある各種タイプ → width_px/height_px を優先、なければ min_*_px を採用
  const width = spec.width_px ?? spec.min_width_px ?? spec.max_width_px ?? undefined;
  const height = spec.height_px ?? spec.min_height_px ?? spec.max_height_px ?? undefined;
  return { width: width ?? undefined, height: height ?? undefined, aspectRatio: firstRatio };
}

/** spec が具体的なピクセル指定を持つか (auto-trim のターゲットに使える) */
export function specHasConcreteResolution(spec: AssetSpec | null | undefined): boolean {
  if (!spec) return false;
  const hasFixed = !!(spec.width_px && spec.height_px);
  const hasMin = !!(spec.min_width_px && spec.min_height_px);
  const hasMax = !!(spec.max_width_px && spec.max_height_px);
  return hasFixed || hasMin || hasMax;
}

export type SpecOrientation = 'landscape' | 'portrait' | 'square';

/** spec のピクセル値 or allowed_aspect_ratios[0] から向きを判定 */
export function getSpecOrientation(
  spec: AssetSpec | null | undefined,
): SpecOrientation | null {
  if (!spec) return null;
  const w = spec.width_px ?? spec.min_width_px ?? spec.max_width_px ?? null;
  const h = spec.height_px ?? spec.min_height_px ?? spec.max_height_px ?? null;
  if (w && h) {
    if (w > h * 1.05) return 'landscape';
    if (h > w * 1.05) return 'portrait';
    return 'square';
  }
  const first = spec.allowed_aspect_ratios?.[0];
  if (first) {
    const r = parseRatio(first);
    if (r === null) return null;
    if (r > 1.05) return 'landscape';
    if (r < 0.95) return 'portrait';
    return 'square';
  }
  return null;
}

/**
 * 数値比に最も近いアスペクト比 (モデル対応比の中から選択) を返す。
 * W×H を直接入力したユーザー向けに、プリセット比から最も近いものを自動選択するのに使う。
 * 比較は log(比) の差分で、1 を中心とした均等な距離で評価する。
 */
export function findNearestAspectRatio(
  modelCode: string | undefined | null,
  targetRatio: number,
): string {
  if (!Number.isFinite(targetRatio) || targetRatio <= 0) return '1:1';
  // モデル別の対応セットから選択 (3.1 Flash 系は極端比 4 種も候補に入る)
  const candidates = Array.from(getSupportedAspectRatios(modelCode));
  const logTarget = Math.log(targetRatio);
  let best = candidates[0];
  let bestDiff = Infinity;
  for (const c of candidates) {
    const r = parseRatio(c);
    if (r === null) continue;
    const diff = Math.abs(Math.log(r) - logTarget);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = c;
    }
  }
  return best;
}

/** gcd (ユークリッド互除法) */
function gcd(a: number, b: number): number {
  a = Math.abs(Math.round(a));
  b = Math.abs(Math.round(b));
  while (b !== 0) { [a, b] = [b, a % b]; }
  return a || 1;
}

/** W × H から簡約した 'W:H' 文字列。入力不正時は null */
function simplifyRatio(w: number, h: number): string | null {
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  // 整数比に簡約できない既知の正準比 (1.91:1 等) は GCD 簡約より優先して返す。
  // 例: 1200×628 → gcd=4 で "300:157" になってしまうが、Amazon/SNS 規格としては "1.91:1" が正しい。
  const r = w / h;
  const canonicals: Array<{ key: string; value: number }> = [
    { key: '1.91:1', value: 1.91 },
    { key: '1:1.91', value: 1 / 1.91 },
  ];
  for (const c of canonicals) {
    if (Math.abs(r - c.value) / c.value <= 0.005) return c.key;
  }
  const d = gcd(w, h);
  return `${Math.round(w) / d}:${Math.round(h) / d}`;
}

/**
 * spec 選択時に使うアスペクト比を解決する。
 *
 * spec 比率 (allowed_aspect_ratios[0] 優先、無ければ W×H から導出) を
 * **モデルがネイティブ対応していればそのまま返す**。非対応なら null を返し、
 * 呼び出し側は getModelNativeAspectForOrientation (向きベース) にフォールバックする。
 *
 * - gpt-image-2: 1:3〜3:1 の任意比率をネイティブ出力できるため、ほぼ spec 実比率が通る
 * - 3.1 Flash 系: 14 種に入る比率 (1:4 等) なら spec 実比率で生成 → クロップ不要
 * - Pro / 2.5 × 1:4 のような非対応組み合わせ: null → 向きフォールバック (9:16 等)。
 *   このとき getDefaultAutoTrimForSpec も同じ isAspectRatioSupportedBy 判定で ON に
 *   フォールバックするため、「9:16 で生成 → exact リサイズで spec 比へクロップ」の
 *   従来動作が維持される (判定関数を共有しているので不整合は構造的に起きない)。
 */
export function resolveSpecAspectRatio(
  modelCode: string | null | undefined,
  spec: AssetSpec | null | undefined,
): string | null {
  if (!spec) return null;
  const specRatio = getSpecRatioString(spec);
  if (!specRatio) return null;
  if (isAspectRatioSupportedBy(modelCode, specRatio)) return specRatio;
  // gpt-image-2: 3:1 超の spec 比率 (例: GDN 320x50 = 32:5) でも実比率を返す。
  // provider が 3:1 へクリップして生成し、exact リサイズが実比率へクロップするため、
  // 向きフォールバック (16:9) で生成するよりクロップの切り捨てが少なく実態に近い。
  // UI 上も spec の実比率が表示され、非対応警告 (buildAspectRatioSupportMessage) が併記される。
  if (getModelProvider(modelCode) === 'openai') return specRatio;
  // Gemini: 離散プリセット外は最も近い対応比率へスナップする (例: 320x50 = 6.4:1 → 8:1)。
  // 向きベースの 16:9/9:16 へ落とすより実比率に近く、exact リサイズのクロップ切り捨てが少ない。
  // (スナップ先はネイティブ比なので、この選択に非対応警告は出ない)
  const specValue = parseRatio(specRatio);
  if (specValue !== null) return findNearestAspectRatio(modelCode, specValue);
  return null;
}

/**
 * 指定オリエンテーションに対するモデルのネイティブアスペクト比を返す。
 * Gemini / gpt-image-2 ともに 16:9 / 9:16 / 1:1 を返す。
 */
export function getModelNativeAspectForOrientation(
  _modelCode: string | undefined | null,
  orientation: SpecOrientation,
): string {
  if (orientation === 'landscape') return '16:9';
  if (orientation === 'portrait') return '9:16';
  return '1:1';
}

/** 解像度ラベル (Gemini) の長辺近似値 — 表に無い組み合わせのフォールバック用 (512px は 2026-10-08 に廃止) */
const GEMINI_MAX_EDGE: Record<string, number> = {
  '1K': 1280, '2K': 1920, '4K': 3840,
};

/**
 * Nano Banana 2.1 (= Gemini 3.1 Flash Image と同一寸法) の実出力サイズ表。
 * 出典: ai.google.dev/gemini-api/docs/image-generation の「アスペクト比と画像サイズ」表
 * (3.1 Flash 分は 2026-07-30 取得。Nano Banana 2.1 のモデル別タブで同一寸法を 2026-10-08 に確認)。
 * キーは `${aspect_ratio}::${image_size}`。512px (3.1 Flash 固有) は 2026-10-08 の廃止で表から外した。
 *
 * 長辺は比率ごとに異なり「解像度ラベル → 長辺固定」では表現できない
 * (1K の長辺は 1:1=1024 / 16:9=1376 / 21:9=1584 / 8:1=3072)。
 *
 * 実測との差異 (2026-07-30 実 API・3.1 Flash):
 * - 1:8 × 1K: 公式表 384x3072 に対し実測 352x2928。極端比は公式値と数%ずれることがある。
 *   ここは公式値を正本として据え置く (予測のズレは「安全側に非効率」に留まり、
 *   規約違反は backend の生成後寸法ガードが実寸法で防ぐ)
 */
const NANO_BANANA_21_OUTPUT_SIZES: Record<string, [number, number]> = {
  '1:1::1K': [1024, 1024], '1:1::2K': [2048, 2048], '1:1::4K': [4096, 4096],
  '1:4::1K': [512, 2048], '1:4::2K': [1024, 4096], '1:4::4K': [2048, 8192],
  '1:8::1K': [384, 3072], '1:8::2K': [768, 6144], '1:8::4K': [1536, 12288],
  '2:3::1K': [848, 1264], '2:3::2K': [1696, 2528], '2:3::4K': [3392, 5056],
  '3:2::1K': [1264, 848], '3:2::2K': [2528, 1696], '3:2::4K': [5056, 3392],
  '3:4::1K': [896, 1200], '3:4::2K': [1792, 2400], '3:4::4K': [3584, 4800],
  '4:1::1K': [2048, 512], '4:1::2K': [4096, 1024], '4:1::4K': [8192, 2048],
  '4:3::1K': [1200, 896], '4:3::2K': [2400, 1792], '4:3::4K': [4800, 3584],
  '4:5::1K': [928, 1152], '4:5::2K': [1856, 2304], '4:5::4K': [3712, 4608],
  '5:4::1K': [1152, 928], '5:4::2K': [2304, 1856], '5:4::4K': [4608, 3712],
  '8:1::1K': [3072, 384], '8:1::2K': [6144, 768], '8:1::4K': [12288, 1536],
  '9:16::1K': [768, 1376], '9:16::2K': [1536, 2752], '9:16::4K': [3072, 5504],
  '16:9::1K': [1376, 768], '16:9::2K': [2752, 1536], '16:9::4K': [5504, 3072],
  '21:9::1K': [1584, 672], '21:9::2K': [3168, 1344], '21:9::4K': [6336, 2688],
};

/**
 * Gemini モデル別の実出力サイズ表。
 * 出力サイズはモデルごとに異なる (16:9×1K は 3.1 Flash=1376x768 / 2.5 Flash=1344x768) ため、
 * 単一表を Gemini 経路全体へ適用してはならない。
 *
 * Nano Banana 2.1 (標準) を登録する。廃止済みの 3.1 Flash (preview / GA 両コード) は同一寸法なので
 * 旧素材のメタデータ表示用に同じ表へ向ける。
 * 未登録モデル・表に無い組み合わせは GEMINI_MAX_EDGE の長辺近似へフォールバックする
 * (Pro / 2.5 / Lite の公式表転記は別タスク。近似の過小評価は安全側に非効率なだけ)。
 *
 * Lite は 1:4 の対応こそ 2026-07-30 に実 API で確認したが、実測 512x2064 が
 * 3.1 Flash の 512x2048 と一致しないため、表は登録せず近似フォールバックに任せる。
 */
const GEMINI_OUTPUT_SIZES_BY_MODEL: Record<string, Record<string, [number, number]>> = {
  'gemini-nano-banana-2.1': NANO_BANANA_21_OUTPUT_SIZES,
  'gemini-3.1-flash-image-preview': NANO_BANANA_21_OUTPUT_SIZES, // 廃止済み (2026-10-08)・同一寸法
  'gemini-3.1-flash-image': NANO_BANANA_21_OUTPUT_SIZES, // 同上 (GA コード)
};

/** gpt-image-* の長辺ピクセル目標 — backend openai_image_provider._LONG_EDGE_BY_SIZE と同期 */
const OPENAI_V2_LONG_EDGE: Record<string, number> = {
  '1K': 1024, '2K': 2048, '4K': 3840,
};
// OpenAI の寸法制約 — backend openai_image_provider の _OPENAI_SIZE_* と同期
// (両辺 16 の倍数・総画素 [655,360, 8,294,400]・比率 1:3〜3:1)。
const OPENAI_SIZE_ALIGN = 16;
const OPENAI_SIZE_MIN_PIXELS = 655_360;
const OPENAI_SIZE_MAX_PIXELS = 8_294_400;
const OPENAI_MAX_RATIO = 3.0;

/**
 * Python の round() (銀行家の丸め: 中間値は偶数側) の移植。
 * JS の Math.round は正の中間値を常に切り上げるため、backend と寸法がずれる
 * (例: 128:81 × 1K は 648/16 = 40.5 → Python 40 (640px) / Math.round 41 (656px))。
 */
function roundHalfEven(x: number): number {
  const f = Math.floor(x);
  const d = x - f;
  if (d < 0.5) return f;
  if (d > 0.5) return f + 1;
  return f % 2 === 0 ? f : f + 1;
}

/** backend openai_image_provider._round_to_multiple の移植 (round は Python 互換の half-even) */
function roundToMultiple(value: number, multiple: number, mode: 'round' | 'ceil' | 'floor' = 'round'): number {
  if (value <= multiple) return multiple;
  const q = value / multiple;
  if (mode === 'ceil') return Math.ceil(q) * multiple;
  if (mode === 'floor') return Math.max(1, Math.floor(q)) * multiple;
  return roundHalfEven(q) * multiple;
}

/**
 * backend openai_image_provider._enforce_size_constraints の移植。
 * 丸め後の (W, H) を比率 3:1 以内・総画素 [MIN, MAX] 内へ補正する (短辺引き上げ優先・最大 32 回)。
 */
function enforceOpenAISizeConstraints(w: number, h: number): [number, number] {
  let wInt = w;
  let hInt = h;
  for (let i = 0; i < 32; i++) {
    const shortIsW = wInt < hInt;
    let shortE = shortIsW ? wInt : hInt;
    let longE = shortIsW ? hInt : wInt;
    if (shortE <= 0) break;
    if (longE / shortE > OPENAI_MAX_RATIO) {
      shortE += OPENAI_SIZE_ALIGN;
    } else if (wInt * hInt > OPENAI_SIZE_MAX_PIXELS) {
      longE -= OPENAI_SIZE_ALIGN;
    } else if (wInt * hInt < OPENAI_SIZE_MIN_PIXELS) {
      longE += OPENAI_SIZE_ALIGN;
    } else {
      break;
    }
    wInt = shortIsW ? shortE : longE;
    hInt = shortIsW ? longE : shortE;
  }
  return [wInt, hInt];
}

/**
 * gpt-image-* が実際に返す寸法を backend openai_image_provider._aspect_ratio_to_size と同じ手順で予測する。
 * 1. 比率を 3:1〜1:3 にクリップ
 * 2. 長辺 (OPENAI_V2_LONG_EDGE) × 比率を 16 の倍数に round
 * 3. 総画素が上限超なら floor でスケールダウン、下限未満なら ceil でスケールアップ
 * 4. 補正後の再検査 (enforceOpenAISizeConstraints)
 * 例: 4K × 1:1 は 3840x3840 ではなく総画素上限で 2880x2880、1K × 9:16 は 576x1024 ではなく下限補正で 608x1088。
 * 媒体規格の推奨判定 (docs/requirements/media-spec-recommended-size-warning-plan.md §3.0) はこの値に依存する。
 */
function predictOpenAIOutputSize(ratio: number, imageSize: string | null | undefined): { width: number; height: number } {
  let r = ratio;
  if (r > OPENAI_MAX_RATIO) r = OPENAI_MAX_RATIO;
  else if (r < 1 / OPENAI_MAX_RATIO) r = 1 / OPENAI_MAX_RATIO;
  const longEdge = OPENAI_V2_LONG_EDGE[imageSize || '1K'] ?? 1024;
  const wPx = r >= 1 ? longEdge : longEdge * r;
  const hPx = r >= 1 ? longEdge / r : longEdge;
  // backend は int(round(w_px)) → _round_to_multiple。どちらも Python の half-even 丸め
  let wInt = roundToMultiple(roundHalfEven(wPx), OPENAI_SIZE_ALIGN);
  let hInt = roundToMultiple(roundHalfEven(hPx), OPENAI_SIZE_ALIGN);
  const total = wInt * hInt;
  if (total > OPENAI_SIZE_MAX_PIXELS) {
    const scale = Math.sqrt(OPENAI_SIZE_MAX_PIXELS / total);
    wInt = roundToMultiple(Math.trunc(wInt * scale), OPENAI_SIZE_ALIGN, 'floor');
    hInt = roundToMultiple(Math.trunc(hInt * scale), OPENAI_SIZE_ALIGN, 'floor');
  } else if (total < OPENAI_SIZE_MIN_PIXELS) {
    const scale = Math.sqrt(OPENAI_SIZE_MIN_PIXELS / total);
    wInt = roundToMultiple(Math.ceil(wInt * scale), OPENAI_SIZE_ALIGN, 'ceil');
    hInt = roundToMultiple(Math.ceil(hInt * scale), OPENAI_SIZE_ALIGN, 'ceil');
  }
  [wInt, hInt] = enforceOpenAISizeConstraints(wInt, hInt);
  return { width: wInt, height: hInt };
}

/**
 * モデル別: 解像度ラベル → 生成される長辺ピクセル (近似)。
 * 同じ「1K」でも Gemini は 1280、gpt-image-2 は 1024 と実長辺が異なるため、
 * 媒体規格の要求ピクセルに対する充足判定はこのヘルパーを使う。
 */
export function getModelResolutionEdge(
  modelCode: string | null | undefined,
  sizeLabel: string,
): number | null {
  const provider = getModelProvider(modelCode);
  const map = provider === 'openai' ? OPENAI_V2_LONG_EDGE : GEMINI_MAX_EDGE;
  return map[sizeLabel] ?? null;
}

/**
 * API が実際に返す想定画素サイズを推定する (UI プレビュー用 / サイズガードの調停用)。
 * - gpt-image-2: aspect_ratio + image_size から長辺近似で導出 (16 の倍数に丸める)
 * - Gemini: モデル別の公式実出力サイズ表を引く。表に無い組み合わせは長辺近似へフォールバック
 */
export function getApiOutputPixelSize(
  modelCode: string | null | undefined,
  aspectRatio: string | null | undefined,
  imageSize: string | null | undefined,
): { width: number; height: number } | null {
  if (!aspectRatio || aspectRatio === 'none') return null;
  const provider = getModelProvider(modelCode);
  if (provider === 'openai') {
    // gpt-image-*: backend と同じ手順 (比率クリップ・16px 丸め・総画素制約・再検査) で予測する
    const r = parseRatio(aspectRatio);
    if (r === null) return null;
    return predictOpenAIOutputSize(r, imageSize);
  }
  // Gemini: まず公式実出力サイズ表を引く (モデル別)
  const sizeLabel = imageSize || '2K';
  const table = GEMINI_OUTPUT_SIZES_BY_MODEL[modelCode as string];
  const exact = table?.[`${aspectRatio}::${sizeLabel}`];
  if (exact) return { width: exact[0], height: exact[1] };
  // 表に無い組み合わせ (未登録モデル / プリセット外比率) は長辺近似へフォールバック
  const r = parseRatio(aspectRatio);
  if (r === null) return null;
  const edge = GEMINI_MAX_EDGE[sizeLabel] ?? 1920;
  if (r >= 1) {
    return { width: edge, height: Math.round(edge / r) };
  }
  return { width: Math.round(edge * r), height: edge };
}

// ---------------------------------------------------------------------------
// 媒体規格サイズガード (docs/requirements/media-spec-size-guard-plan.md)
// min/max はリサイズの目標値ではなく「レンジを外れたときだけ押し込める安全網」。
// 実際のクランプは backend が payload.media_spec から境界を導出して常時適用する。
// フロント側は (1) auto_trim_resolution のデフォルト値と (2) 生成前の解像度調停のみを担う。
// ---------------------------------------------------------------------------

/** ガード対象 (min/max 境界を持つ) の size_constraint_type */
const GUARDED_CONSTRAINT_TYPES = new Set(['min', 'max', 'range', 'min_with_aspect', 'max_with_aspect']);

export type SpecGuardBounds = {
  minWidth: number | null;
  minHeight: number | null;
  maxWidth: number | null;
  maxHeight: number | null;
};

/**
 * spec からガード境界を導出する。ガード対象外 (fixed / aspect_ratio_only 等) は null。
 * 制約タイプに応じた側の境界のみ採用する (min 系 spec に max_*_px が残っていても無視)。
 */
export function getSpecGuardBounds(spec: AssetSpec | null | undefined): SpecGuardBounds | null {
  if (!spec) return null;
  const t = spec.size_constraint_type;
  if (!GUARDED_CONSTRAINT_TYPES.has(t)) return null;
  const useMin = t === 'min' || t === 'min_with_aspect' || t === 'range';
  const useMax = t === 'max' || t === 'max_with_aspect' || t === 'range';
  const bounds: SpecGuardBounds = {
    minWidth: useMin ? spec.min_width_px ?? null : null,
    minHeight: useMin ? spec.min_height_px ?? null : null,
    maxWidth: useMax ? spec.max_width_px ?? null : null,
    maxHeight: useMax ? spec.max_height_px ?? null : null,
  };
  if (bounds.minWidth == null && bounds.minHeight == null && bounds.maxWidth == null && bounds.maxHeight == null) {
    return null;
  }
  return bounds;
}

/** 出力寸法が境界を満たすか (未指定の辺は判定から除外) */
export function outputSatisfiesSpecBounds(
  size: { width: number; height: number },
  bounds: SpecGuardBounds,
): boolean {
  if (bounds.minWidth != null && size.width < bounds.minWidth) return false;
  if (bounds.minHeight != null && size.height < bounds.minHeight) return false;
  if (bounds.maxWidth != null && size.width > bounds.maxWidth) return false;
  if (bounds.maxHeight != null && size.height > bounds.maxHeight) return false;
  return true;
}

/** spec の比率表現。allowed_aspect_ratios[0] 優先、無ければ px 指定から導出 */
export function getSpecRatioString(spec: AssetSpec): string | null {
  const firstAllowed = spec.allowed_aspect_ratios?.[0];
  if (firstAllowed && firstAllowed !== 'none') return firstAllowed;
  const w = spec.width_px ?? spec.min_width_px ?? spec.max_width_px ?? null;
  const h = spec.height_px ?? spec.min_height_px ?? spec.max_height_px ?? null;
  if (!w || !h) return null;
  return simplifyRatio(w, h);
}

/**
 * spec × モデルから auto_trim_resolution の初期値を決める。
 * - spec なし / fixed / aspect_ratio_only: true (従来デフォルト)
 * - min / max / range 系: false (ガード方式。境界は目標値ではない)
 * - 例外: spec 比率をモデルがネイティブ出力できない場合は true
 *   (拡縮のみのガードでは比率ズレを直せないため exact リサイズにフォールバック。
 *    寸法ガード自体は backend が常時適用する)
 */
export function getDefaultAutoTrimForSpec(
  modelCode: string | null | undefined,
  spec: AssetSpec | null | undefined,
): boolean {
  if (!spec) return true;
  if (!GUARDED_CONSTRAINT_TYPES.has(spec.size_constraint_type)) return true;
  const ratio = getSpecRatioString(spec);
  if (ratio && !isAspectRatioSupportedBy(modelCode, ratio)) return true;
  return false;
}

/**
 * ガード対象 spec 向けの解像度調停。
 * 現在のバケットの予測出力が境界を満たすなら null (変更不要)。
 * 満たさない場合、境界を満たすバケットのうち現在に最も近いもの (長辺の対数距離) を返す。
 * 満たすバケットが存在しなければ null (現状維持し、生成後の寸法ガードに委ねる)。
 * 「変更先は必ず feasible」なので、この関数を繰り返し適用しても往復しない。
 */
export function chooseFeasibleImageSize(args: {
  modelCode: string | null | undefined;
  aspectRatio: string | null | undefined;
  currentSize: string;
  candidateSizes: string[];
  bounds: SpecGuardBounds;
}): string | null {
  const { modelCode, aspectRatio, currentSize, candidateSizes, bounds } = args;
  const currentOut = getApiOutputPixelSize(modelCode, aspectRatio, currentSize);
  if (!currentOut) return null;
  if (outputSatisfiesSpecBounds(currentOut, bounds)) return null;
  const feasible = candidateSizes.filter((size) => {
    const out = getApiOutputPixelSize(modelCode, aspectRatio, size);
    return !!out && outputSatisfiesSpecBounds(out, bounds);
  });
  if (feasible.length === 0) return null;
  const currentEdge = getModelResolutionEdge(modelCode, currentSize) ?? 1;
  let best: string | null = null;
  let bestDiff = Infinity;
  for (const size of feasible) {
    const edge = getModelResolutionEdge(modelCode, size);
    if (edge == null) continue;
    const diff = Math.abs(Math.log(edge / Math.max(currentEdge, 1)));
    if (diff < bestDiff) {
      bestDiff = diff;
      best = size;
    }
  }
  return best === currentSize ? null : best;
}

export type AutoTrimContext = {
  modelCode: string | undefined | null;
  userAspectRatio: string | undefined | null;
  spec: AssetSpec | null | undefined;
  customWidth?: number;
  customHeight?: number;
};

/**
 * auto-trim チェックをグレーアウト表示すべきか (実際には発動しない)。
 *
 * ルール:
 *   グレー = (user_aspect が API 出力と一致) AND (spec に解像度情報無し) AND (カスタム入力無し)
 *   → つまり、発動するのは比率の不一致を埋める or 具体的な目標ピクセルへの正規化 の少なくとも一方がある場合。
 */
export function shouldAutoTrimBeGreyed(ctx: AutoTrimContext): boolean {
  const aspectsMatch = doesUserAspectMatchApiOutput(ctx.modelCode, ctx.userAspectRatio);
  const hasSpecResolution = specHasConcreteResolution(ctx.spec);
  const hasCustomResolution = !!(ctx.customWidth && ctx.customHeight);
  return aspectsMatch && !hasSpecResolution && !hasCustomResolution;
}

/** auto-trim が ON のときに実際に発動するか (fires != !greyed。チェック OFF 時は false) */
export function willAutoTrimFire(ctx: AutoTrimContext & { enabled: boolean }): boolean {
  if (!ctx.enabled) return false;
  return !shouldAutoTrimBeGreyed(ctx);
}

/** アスペクト比未対応時の警告メッセージ (モデル別) */
export function buildAspectRatioSupportMessage(
  modelCode: string | undefined | null,
  aspectRatio: string | undefined | null,
): string | null {
  if (!aspectRatio || aspectRatio === 'none') return null;
  if (isAspectRatioSupportedBy(modelCode, aspectRatio)) return null;
  const provider = getModelProvider(modelCode);
  if (provider === 'gemini') {
    // Gemini 非対応比は最も近い対応比で近似出力される
    const target = parseRatio(aspectRatio);
    const nearest = target !== null ? findNearestAspectRatio(modelCode, target) : aspectRatio;
    return `Gemini は ${aspectRatio} をサポートしていないため ${nearest} で生成します。適宜トリミングしてください。`;
  }
  if (provider === 'openai') {
    // gpt-image-2 がサポート外 = 3:1 超の極端な比率
    return `GPT Image は縦横比 3:1 以内のみサポートします。${aspectRatio} は範囲外のため、近い比率に丸められて生成されます。適宜トリミングしてください。`;
  }
  return 'このアスペクト比はモデルに対応していません。';
}

// ---------------------------------------------------------------------------
// 媒体規格の推奨サイズ判定 (docs/requirements/media-spec-recommended-size-warning-plan.md §3.3)
// 推奨は必須条件ではない。生成はブロックせず、推奨に届かないときだけインライン警告を出す。
// 判定対象は「実際の出力寸法 (予測)」で、推奨比率への切り出しは仮定しない。
// ---------------------------------------------------------------------------

/**
 * backend image_postprocess.STANDARD_RESOLUTIONS の複製。
 * auto_trim ON で明示目標が無いとき、backend は image_size からこの表を引いて exact リサイズする (16:9 / 9:16 のみ)。
 */
const STANDARD_RESOLUTIONS: Record<string, [number, number]> = {
  '16:9::1K': [1280, 720], '16:9::2K': [1920, 1080], '16:9::4K': [3840, 2160],
  '9:16::1K': [720, 1280], '9:16::2K': [1080, 1920], '9:16::4K': [2160, 3840],
};
/** backend image_postprocess.ASPECT_RATIO_TOLERANCE の複製 (比率差がこれを超えると Gemini は加工をスキップ) */
const POSTPROCESS_ASPECT_RATIO_TOLERANCE = 0.02;

export type RecommendedShortfallKind =
  | 'output_below_recommended'   // 生成される画像 (加工後) が媒体推奨より小さい
  | 'target_below_recommended'   // リサイズ目標が媒体推奨より小さい (解像度を上げても解決しない)
  | 'upscale_below_recommended'  // 目標は推奨以上だが、加工前の画像が媒体推奨より小さく拡大される (推奨未達)
  | 'upscale_to_target';         // 加工前の画像は媒体推奨を満たすが、手動の大きな目標へ拡大される (情報)

export type RecommendedShortfall = {
  kind: RecommendedShortfallKind;
  recommended: { width: number; height: number; longestEdgeOnly: boolean };
  /** 判定対象の寸法 (output_*: 加工後の予測 / target_*: 目標 / upscale_*: 加工前を目標比率に切り出した寸法) */
  predicted: { width: number; height: number };
  target?: { width: number; height: number };
  /** ガード境界を満たし、この理由を解消する最小の帯。null = 無い */
  suggestedSize: string | null;
};

type SizeWH = { width: number; height: number };

/** 寸法 src を比率 (w/h) に切り出した寸法 (auto_trim の exact リサイズが実際に行うクロップ) */
function cropToRatio(src: SizeWH, ratio: number): SizeWH {
  if (src.width / src.height > ratio) return { width: src.height * ratio, height: src.height };
  return { width: src.width, height: src.width / ratio };
}

/**
 * auto_trim ON・明示目標なしのときの加工後寸法 (backend image_postprocess の目標決定 131〜184 行を写す)。
 * - STANDARD_RESOLUTIONS に無い比率 → 加工スキップ (raw)
 * - 比率差が許容超で Gemini → スキップ (raw)
 * - raw が標準より小さい → 明示目標が無いので拡大せずスキップ (raw)
 * - それ以外 → 標準サイズへ縮小
 */
function predictImplicitPostprocessOutput(
  modelCode: string | null | undefined,
  aspectRatio: string,
  imageSize: string,
  raw: SizeWH,
): SizeWH {
  const standard = STANDARD_RESOLUTIONS[`${aspectRatio}::${imageSize}`];
  if (!standard) return raw;
  const [sw, sh] = standard;
  const isOpenAI = getModelProvider(modelCode) === 'openai';
  const ratioDiff = Math.abs(raw.width / raw.height - sw / sh) / (sw / sh);
  if (ratioDiff > POSTPROCESS_ASPECT_RATIO_TOLERANCE && !isOpenAI) return raw;
  if (raw.width < sw || raw.height < sh) return raw;
  return { width: sw, height: sh };
}

export function evaluateRecommendedShortfall(args: {
  modelCode: string | null | undefined;
  aspectRatio: string | null | undefined;
  currentSize: string;
  candidateSizes: string[];
  spec: AssetSpec;
  autoTrim: boolean;
  target?: { width?: number | null; height?: number | null };
}): RecommendedShortfall | null {
  const { modelCode, aspectRatio, currentSize, candidateSizes, spec, autoTrim, target } = args;
  const rec = getSpecRecommendedSize(spec);
  if (!rec || !aspectRatio || aspectRatio === 'none') return null;
  const longestEdgeOnly = spec.size_constraint_type === 'longest_edge_range';
  const meets = (s: SizeWH): boolean => (
    longestEdgeOnly
      ? Math.max(s.width, s.height) >= rec.width
      : s.width >= rec.width && s.height >= rec.height
  );
  // backend の explicit_target = bool(target_width and target_height) と同じ (片辺のみは未指定扱い)
  const explicitTarget: SizeWH | null = autoTrim && target?.width && target?.height
    ? { width: target.width, height: target.height }
    : null;
  const bounds = getSpecGuardBounds(spec);

  type Verdict = { kind: RecommendedShortfallKind; predicted: SizeWH } | null;
  const judge = (size: string): Verdict | undefined => {
    const raw = getApiOutputPixelSize(modelCode, aspectRatio, size);
    if (!raw) return undefined;
    if (!explicitTarget) {
      const out = autoTrim ? predictImplicitPostprocessOutput(modelCode, aspectRatio, size, raw) : raw;
      return meets(out) ? null : { kind: 'output_below_recommended', predicted: out };
    }
    if (!meets(explicitTarget)) return { kind: 'target_below_recommended', predicted: explicitTarget };
    const cropped = cropToRatio(raw, explicitTarget.width / explicitTarget.height);
    if (!meets(cropped)) return { kind: 'upscale_below_recommended', predicted: cropped };
    if (cropped.width < explicitTarget.width || cropped.height < explicitTarget.height) {
      return { kind: 'upscale_to_target', predicted: cropped };
    }
    return null;
  };

  const current = judge(currentSize);
  if (!current) return null;

  // 理由ごとの到達条件で、ガード境界を満たす上位の帯から提案を選ぶ
  let suggestedSize: string | null = null;
  if (current.kind !== 'target_below_recommended') {
    const currentEdge = getModelResolutionEdge(modelCode, currentSize) ?? 0;
    const sorted = candidateSizes
      .filter((s) => getModelResolutionEdge(modelCode, s) != null)
      .sort((a, b) => getModelResolutionEdge(modelCode, a)! - getModelResolutionEdge(modelCode, b)!);
    for (const size of sorted) {
      if (size === currentSize || (getModelResolutionEdge(modelCode, size) ?? 0) <= currentEdge) continue;
      const raw = getApiOutputPixelSize(modelCode, aspectRatio, size);
      if (!raw) continue;
      if (bounds && !outputSatisfiesSpecBounds(raw, bounds)) continue;
      let reached = false;
      if (current.kind === 'output_below_recommended') {
        reached = judge(size) === null;
      } else if (current.kind === 'upscale_below_recommended') {
        reached = meets(cropToRatio(raw, explicitTarget!.width / explicitTarget!.height));
      } else {
        const cropped = cropToRatio(raw, explicitTarget!.width / explicitTarget!.height);
        reached = cropped.width >= explicitTarget!.width && cropped.height >= explicitTarget!.height;
      }
      if (reached) {
        suggestedSize = size;
        break;
      }
    }
  }

  return {
    kind: current.kind,
    recommended: { width: rec.width, height: rec.height, longestEdgeOnly },
    predicted: { width: Math.round(current.predicted.width), height: Math.round(current.predicted.height) },
    target: explicitTarget ?? undefined,
    suggestedSize,
  };
}
